import { printDebug } from '@/lib/printDebug'
import { withPrintOffset } from '@/lib/printOffset'
import { getPrintOffsetCols } from '@/lib/stationPrinterMap'

/**
 * Single seam between the web app and the Bhookly desktop (Electron) shell.
 *
 * In a normal browser, printing goes through an off-screen iframe + window.print()
 * and lands on the OS default printer (silent only if launched with
 * --kiosk-printing). Inside the desktop app, window.bhookly lets us print
 * silently AND target a specific printer per job — which is what makes
 * per-station routing (bar slip → bar printer, kitchen slip → kitchen printer)
 * possible at all.
 *
 * Callers feature-detect via desktop()/desktopPrint() so the SAME bundle runs
 * in the browser and in the desktop app with no build-time branching.
 *
 * F-FE-N156: the machine-local print nudge ("push slips right by N", the same
 * number the phone's ESC/POS path prefixes as spaces) is applied HERE, to the
 * finished HTML, because this is the one seam every desktop slip crosses —
 * receipts, refunds, credit notes, tax invoices, Z/shift reports, POs, KOTs and
 * the counter relay. Reading it once here beats threading it through every
 * builder, and a slip cannot forget it. See printOffset.ts for the unit.
 */

export interface DesktopPrintResult {
  ok: boolean
  error?: string
}

export interface DesktopBridge {
  isDesktop: true
  appVersion(): Promise<string>
  print(req: {
    html: string
    deviceName?: string
    paperWidthMm?: number
    imageBudgetMs?: number
    printableAreaMm?: number
  }): Promise<DesktopPrintResult>
  getPrinters(): Promise<{ name: string; isDefault: boolean }[]>
  /** Minimize the fullscreen/kiosk POS window to the taskbar. */
  minimize(): Promise<void>
  getTenantOrigin(): Promise<string | null>
  reconfigureTenant(): Promise<void>
  /** Set the window/taskbar icon to the tenant's logo (raster URL). No-op on baked builds. */
  setAppIcon(logoUrl: string): Promise<void>

  // ── Update banner (F-FE-N47) ───────────────────────────────────────────────
  // Optional because a packaged shell older than this frontend won't expose
  // them. That can't happen today (the app serves its own bundled frontend from
  // resources/app-dist, so preload and renderer always ship together) — but the
  // cost of guarding is one `?.` and the cost of being wrong is a white screen
  // on a live till.

  /** A staged update waiting to be applied, or null. */
  getUpdateState?(): Promise<{ version: string } | null>
  /** Quit, install the staged update silently, and relaunch. */
  installUpdate?(): Promise<void>
  /** Subscribe to update-state changes; returns an unsubscribe function. */
  onUpdateState?(cb: (s: { version: string } | null) => void): () => void

  /**
   * Report the resolved page-canvas colour so the shell repaints its window
   * background to match (F-FE-N55). Optional for the same reason as the update
   * channel above: a packaged shell older than this frontend won't expose it,
   * and the cost of being wrong is a navy flash on a live till.
   */
  setThemeCanvas?(hex: string): Promise<void>
}

declare global {
  interface Window {
    bhookly?: DesktopBridge
  }
}

/** The Electron bridge when running inside the desktop app, otherwise null. */
export function desktop(): DesktopBridge | null {
  if (typeof window === 'undefined') return null
  const b = window.bhookly
  return b && b.isDesktop ? b : null
}

/**
 * Silently print finished HTML to a named printer when in the desktop app.
 * Returns true if the desktop bridge took the job (the caller must then skip
 * its browser iframe path); false when not running in the desktop app, in
 * which case the caller falls back to window.print().
 *
 * An empty/undefined deviceName prints to the OS default printer — preserving
 * today's single-printer behaviour for tenants that haven't mapped stations to
 * specific devices.
 */
export function desktopPrint(
  html: string,
  opts: {
    deviceName?: string
    paperWidthMm?: number
    printableAreaMm?: number
    /** Short-circuit the remote-image wait when offline (F-FE-N79). */
    imageBudgetMs?: number
  } = {},
): boolean {
  const d = desktop()
  if (!d) return false
  d.print({
    html: withPrintOffset(html, getPrintOffsetCols()),
    deviceName: opts.deviceName || undefined,
    paperWidthMm: opts.paperWidthMm,
    ...(opts.imageBudgetMs != null ? { imageBudgetMs: opts.imageBudgetMs } : {}),
    printableAreaMm: opts.printableAreaMm,
  })
    .then((r) => {
      if (!r.ok) printDebug.error('desktop', 'silent print failed', r.error)
    })
    .catch((e) => printDebug.error('desktop', 'silent print threw', e))
  return true
}

/**
 * Same job as desktopPrint, but AWAITS the shell's verdict.
 *
 * desktopPrint above is fire-and-forget: it returns true the moment the bridge
 * accepts the request, and a failure only ever reaches printDebug. That is fine
 * for a cashier standing at the printer — they can see the paper — but it is
 * useless to the counter print agent (F-FE-N60), which has to tell the backend
 * whether a relayed slip actually printed. Acking on desktopPrint's return
 * value would report success for every failed job, and the waiter's tablet
 * would go green while nothing came out.
 *
 * Returns null when not running in the desktop app, so callers can distinguish
 * "no bridge" from "the bridge said no".
 */
export async function desktopPrintAsync(
  html: string,
  opts: {
    deviceName?: string
    paperWidthMm?: number
    printableAreaMm?: number
    /** Short-circuit the remote-image wait when offline (F-FE-N79). */
    imageBudgetMs?: number
  } = {},
): Promise<DesktopPrintResult | null> {
  const d = desktop()
  if (!d) return null
  try {
    const r = await d.print({
      html: withPrintOffset(html, getPrintOffsetCols()),
      deviceName: opts.deviceName || undefined,
      ...(opts.imageBudgetMs != null ? { imageBudgetMs: opts.imageBudgetMs } : {}),
      paperWidthMm: opts.paperWidthMm,
      printableAreaMm: opts.printableAreaMm,
    })
    if (!r.ok) printDebug.error('desktop', 'silent print failed', r.error)
    return r
  } catch (e) {
    printDebug.error('desktop', 'silent print threw', e)
    return { ok: false, error: e instanceof Error ? e.message : String(e) }
  }
}
